import { Button, IconButton, Spinner } from '@ui';
import { useEffect, useState } from 'react';
import { AlertIcon, CheckIcon, CloseIcon, EyeIcon, RefreshIcon, StopIcon } from '../icons';
import { uxApi } from './uxApi';
import { mmss, stopUxWatch, uxWatchStatus, type UxTraceFile } from './uxWatch';

type PillState =
  | { kind: 'recording' }
  | { kind: 'saving' }
  | { kind: 'saved'; id: number }
  | { kind: 'error'; message: string; trace: UxTraceFile };

export default function UxWatchPill({ isSuper, onDone, onOpen }: { isSuper: boolean; onDone: () => void; onOpen: () => void }) {
  const [state, setState] = useState<PillState>({ kind: 'recording' });
  const [, tick] = useState(0);

  useEffect(() => {
    if (state.kind !== 'recording') return;
    const timer = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, [state.kind]);

  const send = async (trace: UxTraceFile) => {
    setState({ kind: 'saving' });
    try {
      const res = await uxApi.save(trace);
      setState({ kind: 'saved', id: res.session.id });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Не сохранилось', trace });
    }
  };

  const stop = () => {
    const trace = stopUxWatch();
    if (!trace) return onDone();
    void send(trace);
  };

  const status = uxWatchStatus();
  return (
    <div className={`ux-pill ux-pill-${state.kind}`} data-bug-own="" role="status" aria-live="polite">
      {state.kind === 'recording' && (
        <>
          <span className="ux-dot" aria-hidden />
          <span className="ux-pill-label">Запись</span>
          <span className="ux-pill-time">{mmss(status?.durationMs ?? 0)}</span>
          <Button size="sm" variant="tonal" icon={<StopIcon />} onClick={stop}>
            Стоп
          </Button>
        </>
      )}
      {state.kind === 'saving' && (
        <>
          <Spinner size="sm" label="Сохраняю" />
          <span className="ux-pill-label">Сохраняю…</span>
        </>
      )}
      {state.kind === 'saved' && (
        <>
          <span className="ux-ok">
            <CheckIcon />
          </span>
          <span className="ux-pill-label">Сохранено · разбор идёт</span>
          {isSuper && (
            <Button
              size="sm"
              variant="tonal"
              icon={<EyeIcon />}
              onClick={() => {
                onDone();
                onOpen();
              }}
            >
              Открыть
            </Button>
          )}
          <IconButton size="sm" label="Закрыть" icon={<CloseIcon />} onClick={onDone} />
        </>
      )}
      {state.kind === 'error' && (
        <>
          <span className="ux-err">
            <AlertIcon />
          </span>
          <span className="ux-pill-label" title={state.message}>
            Не сохранилось
          </span>
          <Button size="sm" variant="tonal" icon={<RefreshIcon />} onClick={() => void send(state.trace)}>
            Ещё раз
          </Button>
          <IconButton size="sm" label="Выбросить запись" icon={<CloseIcon />} onClick={onDone} />
        </>
      )}
    </div>
  );
}
